import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, TextField, Dialog, Button, Alert, IconButton } from '@mui/material';
import { i18n } from '../../../../translate/i18n';
import SignatureModal, { ISignature } from './SignatureModal';
import { RootState } from '../../../../store/types';
import addingSignatureSvg from '../../../../assets/adding-signature.svg';
import signatureIcon from '../../../../assets/signature-icon.svg';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import * as actions from '../../../../store/actions';
import { debounce } from 'lodash';
import { EUpdateDraftType, IUpdateServiceDraftBody } from './Checklist';

type Props = {
  isNewData: boolean,
  isMandatory?: boolean,
  createOrUpdateDraft: (body: IUpdateServiceDraftBody) => Promise<void>,
};

const debouncedHandleInputChange = debounce((handleInputChange: () => Promise<void>) => {
	handleInputChange();
}, 1000);

const Signature: React.FC<Props> = ({ isNewData, isMandatory = false, createOrUpdateDraft}) => {
  const dispatch = useDispatch();
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [signaturesCopy, setSignaturesCopy] = useState<{ name: string, complement: string, file: ISignature }[]>([]);
  const loadingSignature = useSelector((state: RootState) => state.createService.loadingSignature);
  const signatures = useSelector((state: RootState) => state.createService.signatures);

  useEffect(() => {
    setSignaturesCopy(signatures);
  }, [signatures]);

  const updateDraft = async (signatureIds: string[] = []) => {
    const signatures = signaturesCopy.map((signature: any) => ({
      name: signature.name,
      complement: signature.complement,
      file: signature.file.signatureId || signature.file._id,
    })).concat(signatureIds.map((signatureId) => ({file: signatureId, name: '', complement: ''})));

    await createOrUpdateDraft({type: EUpdateDraftType.signatures, signatures});

  }

  const setSignatureName = (name: string, index: number) => {
    const signaturesCopy = [...signatures];
    signaturesCopy[index].name = name;
    setSignaturesCopy(signaturesCopy);
    debouncedHandleInputChange(async () => {
      const dispatchName = async () => dispatch(actions.createServiceAddSignatureName(name, index));
      await dispatchName();
      await updateDraft([]);
    })
  }

  const setSignatureComplement = (complement: string, index: number) => {
    const signaturesCopy = [...signatures];
    signaturesCopy[index].complement = complement;
    setSignaturesCopy(signaturesCopy);
    debouncedHandleInputChange(async () => {
      const dispatchComplement = async () => dispatch(actions.createServiceAddSignatureComplement(complement, index));
      await dispatchComplement();
      await updateDraft([]);
    })
  }
  return <>
    <Dialog
      fullWidth
      maxWidth='lg'
      scroll='body'
      open={isSignatureModalOpen}
      onClose={() => setIsSignatureModalOpen(false)}
    >
      <SignatureModal
        isOpen={isSignatureModalOpen}
        onClose={() => setIsSignatureModalOpen(false)}
        createOrUpdateDraft={(signatureIds: string[]) => updateDraft(signatureIds)}
      />
    </Dialog>
    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px'}}>
      <Box sx={styles.sectionTitleContainer}>
        <img src={signatureIcon} style={{ width: '24px', height: '24px'}} />
        <Typography sx={{marginRight: '10px', marginLeft: '5px'}}>
          {i18n.t('typographyFormService.signatures') as string}
        </Typography>
        {isMandatory && <Alert severity='info'>{i18n.t('typographyFormService.mandatorySignature') as string}</Alert>}
      </Box>
    </Box>
    {signatures?.length === 0 && 
      <Box sx={{ marginTop: '20px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'}}>
        <img src={addingSignatureSvg} alt='Representação de assinatura' style={{width: '170px', height: '170px', marginBottom: '10px'}}/>
        <Typography sx={{marginBottom: '10px'}}>{i18n.t('typographyFormService.addSignatures') as string}</Typography>
        <Button onClick={() => setIsSignatureModalOpen(true)} variant='outlined' startIcon={<AddIcon />}>
          {i18n.t('typographyFormService.addSignature') as string}
        </Button>
      </Box>
    }

    { signaturesCopy && signaturesCopy.length > 0 &&
      <Box>
        {signaturesCopy.map((signature, index) => {
          const isSignatureFromWeb = signature?.file?.source === 'web';
          return <Box key={signature.file.signatureId} sx={{padding: '16px', backgroundColor: '#F3F3F3', borderRadius: '8px', width: '100%', marginBottom: '10px', display: 'inline-flex', gap: '16px', flexDirection: 'column', marginTop: '12px' }}>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <Box>
                <Typography sx={{fontSize: 12, color: 'rgba(0, 0, 0, 0.6)', marginBottom: '5px'}}>{i18n.t('typographyFormService.signature') as string}</Typography>
                <img src={signature.file.url} style={isSignatureFromWeb ? styles.signatureWeb : styles.signatureApp} />
              </Box>
              <IconButton sx={{alignSelf: 'flex-start'}} onClick={() => dispatch(actions.createServiceRemoveSignature(index))}>
                <DeleteIcon sx={{ fontSize: '35px', color: 'rgba(0, 0, 0, 0.56)', }} />
              </IconButton>
            </Box>
            <TextField
              variant="standard"
              label={i18n.t('textFieldFormService.accountable') as string}
              name={'signature-name' + index}
              value={signature.name}
              margin='none'
              onChange={(e) => {
                setSignatureName(e.target.value, index);
              }}
              disabled={!isNewData}
              sx={{
                width: '40%',
                opacity: loadingSignature ? 0.3 : 1,
                marginTop: '10px',
                marginBottom: '10px',
              }}
            />
            <TextField
              variant="standard"
              label={i18n.t('typographyFormService.complement') as string }
              name={'signature-complement' + index}
              value={signature.complement}
              onChange={(e) => setSignatureComplement(e.target.value, index)}
              margin='none'
              disabled={!isNewData}
              sx={{
                width: '40%',
                marginTop: '10px',
                opacity: loadingSignature ? 0.3 : 1,
                marginBottom: '10px',
              }}
            />
          </Box>
        })}
        <Button onClick={() => setIsSignatureModalOpen(true)} variant='outlined' startIcon={<AddIcon />}>
          {i18n.t('typographyFormService.addSignature') as string}
        </Button>
      </Box>
    }
  </>
}

const styles = {
  image: {
    width: '121.95px',
    height: '250px',
    borderRadius: '5px',
    transform: 'rotate(-90deg)',
    margin: '-60px 0 -70px 65px',
    backgroundColor: 'white',
  },
  sectionTitleContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  formControl: {
    display: 'flex',
  },
  signatureLabel: {
    marginTop: '12px',
    marginRight: '10px',
    color: 'rgba(0, 0, 0, 0.38)',
    fontSize: '0.8rem',

  },
  signatureApp: {
    width: "60px",
    height: "190px",
    borderRadius: "5px",
    objectFit: "contain" as "contain",
    transform: "rotate(-90deg)",
    margin: "-60px 0 -70px 65px",
    backgroundColor: "white",
  },
  signatureWeb: { width: '190px', height: '60px', backgroundColor: 'white'},
}

export default Signature;